import { useEffect, useState, useRef } from 'react';
import { Clock, Plus, Edit2, Trash2, User, UserX } from 'lucide-react';
import { useToastStore, useUserStore } from '../store';
import { Spinner, EmptyState, Badge, Modal } from '../components/ui';
import { api } from '../utils/api';
import { DAYS_OF_WEEK, type DayOfWeek, type User as UserType } from '../types';

interface ShiftTemplate {
  id: string;
  day_of_week: DayOfWeek;
  start_time: string;
  end_time: string;
  user_id: string | null;
  name: string | null;
  active: number;
  user?: UserType;
}

export function AdminShiftTemplatesPage() {
  const { addToast } = useToastStore();
  const { users, fetchUsers } = useUserStore();
  const [templates, setTemplates] = useState<ShiftTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ShiftTemplate | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>('Monday');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [shiftName, setShiftName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollPositionRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [checkDay, setCheckDay] = useState<DayOfWeek>('Wednesday');
  const [checkStartTime, setCheckStartTime] = useState('09:30');
  const [checkEndTime, setCheckEndTime] = useState('');
  const [checkResult, setCheckResult] = useState<any>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkDate, setCheckDate] = useState(() => {
    // Get next Wednesday
    const today = new Date();
    const day = today.getDay();
    const diff = (3 - day + 7) % 7 || 7; // Wednesday is 3
    const nextWednesday = new Date(today);
    nextWednesday.setDate(today.getDate() + diff);
    return nextWednesday.toISOString().split('T')[0];
  });
  const [shiftExistsResult, setShiftExistsResult] = useState<any>(null);
  const [isCheckingExists, setIsCheckingExists] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchTemplates();
  }, []);

  // Restore scroll position after templates are loaded
  useEffect(() => {
    if (scrollPositionRef.current > 0 && !isLoading) {
      // Use requestAnimationFrame to ensure DOM has fully updated
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: 'instant'
        });
        scrollPositionRef.current = 0; // Reset after restoring
      });
    }
  }, [templates, isLoading]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    const result = await api.getAllShiftTemplates();
    setIsLoading(false);

    if (result.success && result.data) {
      // Cast day_of_week to DayOfWeek type and ensure name is included
      const dataArray = Array.isArray(result.data) ? result.data : [];
      setTemplates(dataArray.map(t => ({
        ...t,
        day_of_week: t.day_of_week as DayOfWeek,
        name: (t as any).name || null
      })));
    } else {
      addToast(result.error || 'Failed to load permanent shifts', 'error');
    }
  };

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleAddTemplate = async () => {
    setIsSubmitting(true);
    const result = await api.addShiftTemplate(
      selectedDay,
      startTime,
      endTime,
      selectedUserId || null,
      shiftName || null
    );
    setIsSubmitting(false);

    if (result.success) {
      addToast(selectedUserId ? 'Permanent shift added!' : 'Available shift template added!', 'success');
      fetchTemplates();
      closeAddModal();
    } else {
      addToast(result.error || 'Failed to add permanent shift', 'error');
    }
  };

  const handleEditTemplate = (template: ShiftTemplate) => {
    setEditingTemplate(template);
    setSelectedDay(template.day_of_week);
    setStartTime(template.start_time);
    setEndTime(template.end_time);
    setSelectedUserId(template.user_id || '');
    setShiftName((template as any).name || '');
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;

    // Save current scroll position before closing modal
    scrollPositionRef.current = window.scrollY;

    setIsSubmitting(true);
    const result = await api.updateShiftTemplate(
      editingTemplate.id,
      startTime,
      endTime,
      selectedUserId || null,
      shiftName || null
    );
    setIsSubmitting(false);

    if (result.success) {
      addToast('Permanent shift updated!', 'success');
      closeEditModal();
      fetchTemplates();
    } else {
      addToast(result.error || 'Failed to update permanent shift', 'error');
    }
  };

  const handleRemoveTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to remove this permanent shift? This will prevent new shifts from being generated for this time slot.')) {
      return;
    }

    const result = await api.removeShiftTemplate(id);

    if (result.success) {
      addToast('Permanent shift removed', 'success');
      fetchTemplates();
    } else {
      addToast(result.error || 'Failed to remove permanent shift', 'error');
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedDay('Monday');
    setStartTime('09:00');
    setEndTime('17:00');
    setSelectedUserId('');
    setShiftName('');
  };

  const closeEditModal = () => {
    setEditingTemplate(null);
    setSelectedDay('Monday');
    setStartTime('09:00');
    setEndTime('17:00');
    setSelectedUserId('');
    setShiftName('');
  };

  // Group templates by day
  const safeTemplates = (templates && Array.isArray(templates)) ? templates : [];
  const safeUsers = (users && Array.isArray(users)) ? users : [];
  
  const templatesByDay = safeTemplates.reduce((acc, template) => {
    if (!acc[template.day_of_week]) {
      acc[template.day_of_week] = { active: [], inactive: [] };
    }
    if (template.active === 1) {
      acc[template.day_of_week].active.push(template);
    } else {
      acc[template.day_of_week].inactive.push(template);
    }
    return acc;
  }, {} as Record<DayOfWeek, { active: ShiftTemplate[]; inactive: ShiftTemplate[] }>);

  const activeTemplates = safeTemplates.filter(t => t.active === 1);
  const hasAnyTemplates = activeTemplates.length > 0;
  const activeUsers = safeUsers.filter(u => u.status === 'Active');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const handleCheckShift = async () => {
    setIsChecking(true);
    const result = await api.checkShiftSource(checkDay, checkStartTime, checkEndTime || undefined);
    setIsChecking(false);
    
    if (result.success && result.data) {
      setCheckResult(result.data);
    } else {
      addToast(result.error || 'Failed to check shift', 'error');
      setCheckResult(null);
    }
  };

  return (
    <div ref={containerRef}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Permanent Shifts</h1>
          <p className="text-slate-500 mt-1">
            Define which shifts are automatically generated each week. Assign users to create permanent shifts, or leave unassigned for available shifts.
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
          <Plus className="w-4 h-4" />
          Add Permanent Shift
        </button>
      </div>

      {/* Check Shift Source Tool */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="text-lg font-semibold">Check Shift Source</h2>
          <p className="text-sm text-slate-500 mt-1">Check if a shift exists in permanent shifts or templates (including inactive)</p>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="input-label">Day of Week</label>
              <select
                value={checkDay}
                onChange={(e) => setCheckDay(e.target.value as DayOfWeek)}
                className="input"
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="input-label">Start Time</label>
              <input
                type="time"
                value={checkStartTime}
                onChange={(e) => setCheckStartTime(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="input-label">End Time (optional)</label>
              <input
                type="time"
                value={checkEndTime}
                onChange={(e) => setCheckEndTime(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCheckShift}
                disabled={isChecking}
                className="btn btn-primary w-full"
              >
                {isChecking ? 'Checking...' : 'Check Shift'}
              </button>
            </div>
          </div>

          {checkResult && (
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Permanent Shifts Found: {checkResult.permanentShifts.length}</h3>
                {checkResult.permanentShifts.length === 0 ? (
                  <p className="text-sm text-slate-500">No permanent shifts found</p>
                ) : (
                  <div className="space-y-2">
                    {checkResult.permanentShifts.map((ps: any) => (
                      <div key={ps.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{ps.userName} ({ps.userEmail})</p>
                            <p className="text-sm text-slate-600">{ps.startTime} - {ps.endTime}</p>
                            {ps.name && <p className="text-sm text-slate-500">Name: {ps.name}</p>}
                          </div>
                          <div className="text-right">
                            <Badge variant={ps.active ? 'success' : 'warning'}>
                              {ps.active ? 'Active' : 'Inactive'}
                            </Badge>
                            {ps.userStatus !== 'Active' && (
                              <Badge variant="danger" className="mt-1 block">User: {ps.userStatus}</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">ID: {ps.id}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Shift Templates Found: {checkResult.shiftTemplates.length}</h3>
                {checkResult.shiftTemplates.length === 0 ? (
                  <p className="text-sm text-slate-500">No shift templates found</p>
                ) : (
                  <div className="space-y-2">
                    {checkResult.shiftTemplates.map((st: any) => (
                      <div key={st.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{st.userName || 'Unassigned'}</p>
                            <p className="text-sm text-slate-600">{st.startTime} - {st.endTime}</p>
                            {st.name && <p className="text-sm text-slate-500">Name: {st.name}</p>}
                          </div>
                          <div className="text-right">
                            <Badge variant={st.active ? 'success' : 'warning'}>
                              {st.active ? 'Active' : 'Inactive'}
                            </Badge>
                            {st.userId && st.userStatus !== 'Active' && (
                              <Badge variant="danger" className="mt-1 block">User: {st.userStatus}</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">ID: {st.id}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Check if Shift Exists on Schedule */}
      {checkResult && checkResult.permanentShifts.length > 0 && (
        <div className="card mb-6">
          <div className="card-header">
            <h2 className="text-lg font-semibold">Check if Shift Exists on Schedule</h2>
            <p className="text-sm text-slate-500 mt-1">Check if the permanent shift was actually created on a specific date</p>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="input-label">Date</label>
                <input
                  type="date"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                  className="input"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={async () => {
                    setIsCheckingExists(true);
                    const result = await api.checkShiftExists(
                      checkDate,
                      checkResult.permanentShifts[0].startTime,
                      checkResult.permanentShifts[0].endTime
                    );
                    setIsCheckingExists(false);
                    if (result.success) {
                      setShiftExistsResult(result.data);
                    } else {
                      addToast(result.error || 'Failed to check shift', 'error');
                    }
                  }}
                  disabled={isCheckingExists}
                  className="btn btn-primary w-full"
                >
                  {isCheckingExists ? 'Checking...' : 'Check if Exists'}
                </button>
              </div>
              <div className="flex items-end">
                <button
                  onClick={async () => {
                    if (!confirm('This will regenerate all shifts for the next 14 days. Continue?')) return;
                    setIsCheckingExists(true);
                    const result = await api.regenerateShifts();
                    setIsCheckingExists(false);
                    if (result.success) {
                      addToast('Shifts regenerated successfully!', 'success');
                      // Re-check after regeneration
                      setTimeout(() => {
                        handleCheckShift();
                        api.checkShiftExists(
                          checkDate,
                          checkResult.permanentShifts[0].startTime,
                          checkResult.permanentShifts[0].endTime
                        ).then(r => {
                          if (r.success) setShiftExistsResult(r.data);
                        });
                      }, 1000);
                    } else {
                      addToast(result.error || 'Failed to regenerate shifts', 'error');
                    }
                  }}
                  disabled={isCheckingExists}
                  className="btn btn-secondary w-full"
                >
                  Regenerate Shifts
                </button>
              </div>
            </div>

            {shiftExistsResult && (
              <div className="mt-4">
                {shiftExistsResult.exists ? (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-semibold text-green-900 mb-2">✓ Shift exists on schedule</p>
                    <div className="text-sm text-green-800">
                      <p><strong>Status:</strong> {shiftExistsResult.shift.status}</p>
                      <p><strong>Assignment Type:</strong> {shiftExistsResult.shift.assignmentType}</p>
                      {shiftExistsResult.shift.assignedUsers.length > 0 && (
                        <p><strong>Assigned Users:</strong> {shiftExistsResult.shift.assignedUsers.map((u: any) => u.displayName || u.username).join(', ')}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="font-semibold text-red-900 mb-2">✗ Shift does NOT exist on schedule</p>
                    <p className="text-sm text-red-800">
                      The permanent shift exists but the actual shift was not created. Click "Regenerate Shifts" to create it.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!hasAnyTemplates ? (
        <div className="card">
          <EmptyState
            icon={<Clock className="w-12 h-12 mx-auto" />}
            title="No permanent shifts"
            description="Add permanent shifts to control which shifts are generated each week. Assign a user to create a permanent shift, or leave unassigned for available shifts."
            action={
              <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
                <Plus className="w-4 h-4" />
                Add First Permanent Shift
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-6">
          {DAYS_OF_WEEK.map(day => {
            const dayTemplates = templatesByDay[day];
            if (!dayTemplates || (dayTemplates.active.length === 0 && dayTemplates.inactive.length === 0)) {
              return null;
            }
            
            return (
              <div key={day} className="card">
                <div className="card-header">
                  <h2 className="font-semibold text-slate-900">{day}</h2>
                  <div className="flex items-center gap-2">
                    {dayTemplates.active.length > 0 && (
                      <Badge variant="success">{dayTemplates.active.length} active</Badge>
                    )}
                    {dayTemplates.inactive.length > 0 && (
                      <Badge variant="danger">{dayTemplates.inactive.length} inactive</Badge>
                    )}
                    <button
                      onClick={() => {
                        setSelectedDay(day);
                        setShowAddModal(true);
                      }}
                      className="btn btn-ghost btn-sm"
                      title={`Add permanent shift for ${day}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {dayTemplates.active.length === 0 && dayTemplates.inactive.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No permanent shifts for this day</p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {/* Active templates */}
                      {dayTemplates.active.map(template => (
                        <div key={template.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border-2 border-slate-200">
                          <div className="flex-1 min-w-0">
                            {template.name && (
                              <p className="font-semibold text-slate-900 truncate mb-1">
                                {template.name}
                              </p>
                            )}
                            <p className="font-medium text-slate-900 truncate">
                              {formatTime(template.start_time)} - {formatTime(template.end_time)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {template.start_time} - {template.end_time}
                            </p>
                            {template.user_id && template.user ? (
                              <div className="flex items-center gap-1 mt-2">
                                <User className="w-3 h-3 text-blue-500" />
                                <span className="text-xs text-blue-600 font-medium truncate">
                                  {template.user.display_name || template.user.username}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 mt-2">
                                <UserX className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-500">Available</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <button
                              onClick={() => handleEditTemplate(template)}
                              className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit permanent shift"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveTemplate(template.id)}
                              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove permanent shift"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Inactive templates */}
                      {dayTemplates.inactive.map(template => (
                        <div key={template.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border-2 border-slate-200 opacity-60">
                          <div className="flex-1 min-w-0">
                            {template.name && (
                              <p className="font-semibold text-slate-900 truncate mb-1">
                                {template.name}
                              </p>
                            )}
                            <p className="font-medium text-slate-900 truncate">
                              {formatTime(template.start_time)} - {formatTime(template.end_time)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {template.start_time} - {template.end_time}
                            </p>
                            {template.user_id && template.user ? (
                              <div className="flex items-center gap-1 mt-2">
                                <User className="w-3 h-3 text-blue-500" />
                                <span className="text-xs text-blue-600 font-medium truncate">
                                  {template.user.display_name || template.user.username}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 mt-2">
                                <UserX className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-500">Available</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              api.addShiftTemplate(template.day_of_week, template.start_time, template.end_time, template.user_id).then(() => fetchTemplates());
                            }}
                            className="p-2 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Reactivate permanent shift"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title="Add Permanent Shift"
      >
        <div className="space-y-4">
          <div>
            <label className="input-label">Day of Week</label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value as DayOfWeek)}
              className="input"
            >
              {DAYS_OF_WEEK.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="input-label">Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="input-label">End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="input-label">Assign to User (Optional)</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="input"
            >
              <option value="">Leave as Available (No Assignment)</option>
              {activeUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.display_name || user.username} ({user.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {selectedUserId 
                ? 'This will create a permanent shift assigned to the selected user.'
                : 'This will create an available shift that can be requested by any user.'}
            </p>
          </div>

          <div>
            <label className="input-label">Shift Name (optional)</label>
            <input
              type="text"
              value={shiftName}
              onChange={(e) => setShiftName(e.target.value)}
              className="input"
              placeholder="e.g., Morning Shift, Night Shift"
            />
          </div>

          <p className="text-sm text-slate-500">
            Shifts with this time slot will be automatically generated each {selectedDay}.
            {selectedUserId && ' The selected user will be automatically assigned to these shifts.'}
          </p>

          <div className="flex gap-3 justify-end">
            <button
              onClick={closeAddModal}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAddTemplate}
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Add Permanent Shift'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editingTemplate !== null}
        onClose={closeEditModal}
        title="Edit Permanent Shift"
      >
        {editingTemplate && (
          <div className="space-y-4">
            <div>
              <label className="input-label">Day of Week</label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value as DayOfWeek)}
                className="input"
                disabled
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Day cannot be changed. Delete and recreate to change day.</p>
            </div>

            <div>
              <label className="input-label">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="input-label">End Time</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="input-label">Assign to User (Optional)</label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="input"
              >
                <option value="">Leave as Available (No Assignment)</option>
                {activeUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.display_name || user.username} ({user.email})
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {selectedUserId 
                  ? 'This will create a permanent shift assigned to the selected user.'
                  : 'This will create an available shift that can be requested by any user.'}
              </p>
            </div>

            <div>
              <label className="input-label">Shift Name (optional)</label>
              <input
                type="text"
                value={shiftName}
                onChange={(e) => setShiftName(e.target.value)}
                className="input"
                placeholder="e.g., Morning Shift, Night Shift"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeEditModal}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
